import {AxisScale, DataContext, DataContextType} from '@visx/xychart';
import {useContext} from 'react';

import {assert} from 'Asserts';
import {CartesianPoint} from 'ui-library/charting/Types';
import pickDefined from 'util/pickDefined';
import {MakeFieldsOptional} from 'util/Types';

const SCALE_FIELDS = ['xScale', 'yScale'];

export type VisXContext = MakeFieldsOptional<
  DataContextType<AxisScale, AxisScale, CartesianPoint>,
  'xScale' | 'yScale'
>;

/**
 * Convenience wrapper around VisX's DataContext.
 *
 * Returns an object with the fields of the context specified as input.
 *
 * Throws if called from component not mounted in CartesianPlot component tree, as XYChart parent component is
 * needed for context fields to be defined.
 *
 * xScale/yScale fields may be undefined, as they are not actually defined until data is rendered
 * through components like CartesianPoints. This does not apply to colorScale because we specify colors in the
 * theme, so the scale is initialized with those colors before data is rendered.
 */
export default function useVisXContext<K extends keyof VisXContext>(
  ...fields: Array<K>
): Pick<VisXContext, K> {
  // makeReactContext is not compatible with 3rd party contexts, so we need to use useContext
  // eslint-disable-next-line benchlint/no-react-context
  const context = useContext(DataContext);

  assert(
    fields.filter((field) => !SCALE_FIELDS.includes(field)).every((field) => context[field] != null),
    'useVisXContext called while not in CartesianPlot component tree'
  );
  return pickDefined(context as VisXContext, ...fields);
}
